import StatusBadge from "@shared/components/application/data-display/badges/StatusBadge/StatusBadge.web";
import Tile from "@shared/components/ui-kit/form/Tile/Tile.web";
import TileGroup from "@shared/components/ui-kit/form/TileGroup/TileGroup.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { ShippingMethodsTypeMapIdType } from "@shared/constants/enums/enums-types";
import {
  consolidate,
  mergeMemberships,
} from "@shared/modules/basket/utils/helpers";
import CheckoutDeliveryWithSelectionRequirement from "@shared/modules/checkout/components/delivery-methods/CheckoutDeliveryWithSelectionRequirement/CheckoutDeliveryWithSelectionRequirement.web";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { useTranslation } from "@shared/utils/internationalization";
import { Fragment } from "react";
import PublicCheckoutDeliveryItem from "../PublicCheckoutDeliveryItem/PublicCheckoutDeliveryItem.web";

function PublicCheckoutDeliveryWithSelectionRequirement({
  availableTicketDeliveryTypes,
  mixedShippingTypesItemGroups,
  selectedDeliveryMethod,
  onSelectMethod,
  isDeliveryMethodUpdating,
  allowQuantityChange = true,
}: {
  availableTicketDeliveryTypes: ReadonlyArray<{
    id: string;
    label: string;
  }>;
  mixedShippingTypesItemGroups: Array<{
    shippingTypes: ShippingMethodsTypeMapIdType[];
    itemGroups: TicketItemGroupType[];
  }>;
  selectedDeliveryMethod: Record<string, string>;
  onSelectMethod: (method: string) => void;
  isDeliveryMethodUpdating: boolean;
  allowQuantityChange: boolean;
}) {
  const { t } = useTranslation("checkout");

  return (
    <CheckoutDeliveryWithSelectionRequirement
      groups={mixedShippingTypesItemGroups}
      availableTicketDeliveryTypes={availableTicketDeliveryTypes}
      renderDeliveryMethodGroup={({ group, deliveryMethods, id }) => {
        return (
          <>
            <Box
              display="flex"
              flexDirection="column"
              gap="xl"
              border="1px solid"
              borderColor="gray.400"
              borderRadius="md"
              p="md"
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Text fontWeight={600}>{t("Select delivery method")}</Text>
                <StatusBadge
                  status={selectedDeliveryMethod[id] ? "success" : "warning"}
                  props={{
                    transition: "background-color 0.3s ease, color 0.3s ease",
                  }}
                >
                  {t("Required selection")}
                </StatusBadge>
              </Box>

              <Box>
                <TileGroup
                  variant="single"
                  onChange={onSelectMethod}
                  selected={{
                    [selectedDeliveryMethod[id]]: true,
                  }}
                  flexWrap="wrap"
                  gap="sm"
                >
                  {deliveryMethods.map(({ id, label }) => {
                    return (
                      <Tile
                        key={id}
                        id={id}
                        isLoading={isDeliveryMethodUpdating}
                        flex="1 1 30%"
                        minWidth="fit-content"
                      >
                        <Text whiteSpace="nowrap">
                          {t(label, { ns: "constants" })}
                        </Text>
                      </Tile>
                    );
                  })}
                </TileGroup>
              </Box>

              <Box display="flex" flexDirection="column" gap="xl">
                {consolidate(mergeMemberships(group.itemGroups)).map(
                  (itemGroup) => {
                    return (
                      <Fragment key={itemGroup.id}>
                        <PublicCheckoutDeliveryItem
                          itemGroup={itemGroup}
                          allowQuantityChange={allowQuantityChange}
                        />
                      </Fragment>
                    );
                  }
                )}
              </Box>
            </Box>
          </>
        );
      }}
    />
  );
}

export default PublicCheckoutDeliveryWithSelectionRequirement;
